/**
 * 参数配置 - 接口模块
 *
 * 数据三级模型：栏目（column）> 模块（module）> 参数（param）
 * 业务对应路径前缀统一为 `/setting/{column|module|param}/xxx`
 * 所有接口均使用 POST + JSON Body 形式（即使是查询）
 *
 * 业务 code 约定：res.code === '0' / 0 表示成功（与项目 OK 常量一致）
 * 失败时全局 request 拦截器会自动 toast `res.msg`，业务方仅需根据 code 决定后续流程
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import request from './request'
import type { ParamSource, ParamValueType } from '@/views/paramSetting/types'

const serveName = process.env.VUE_APP_SERVENAME

// ===== 栏目（Column）=====

/**
 * 查询栏目列表
 * POST /setting/column/list
 * @returns { code, data: ColumnVO[] }
 */
export const getSettingColumns = () =>
  request(`${serveName}/setting/column/list`, null, 'POST_JSON', {})

/**
 * 新增栏目
 * POST /setting/column/add
 * @param columnName 栏目名称（前端已做非空校验 + maxlength 30）
 * @returns { code, data: { id: number } }
 */
export const addSettingColumn = (columnName: string) =>
  request(`${serveName}/setting/column/add`, null, 'POST_JSON', { columnName })

/**
 * 删除栏目
 * POST /setting/column/delete
 *
 * **后端校验**：若栏目下还有 del_flag=0 的模块，会返回 msg「请先删除栏目下所有模块」
 * 该错误提示由全局 request 拦截器统一 toast，前端无需重复处理
 */
export const deleteSettingColumn = (id: number) =>
  request(`${serveName}/setting/column/delete`, null, 'POST_JSON', { id })

// ===== 模块（Module）=====

/**
 * 查询栏目下模块列表
 * POST /setting/module/list
 * @returns { code, data: ModuleVO[] }（无序，前端按 sortOrder 排序后展示）
 */
export const getSettingModules = (columnId: number) =>
  request(`${serveName}/setting/module/list`, null, 'POST_JSON', { columnId })

/**
 * 新增 / 编辑模块的请求体
 * id 为 null 时走新增，非空时走编辑
 */
export interface ModuleSavePayload {
  /** null = 新增；非空 = 编辑 */
  id: number | null
  /** 所属栏目 id */
  columnId: number
  /** 模块名称（前端已校验非空） */
  moduleName: string
  /**
   * 模块编码（业务 key）
   * 格式约束：仅支持字母 / 数字 / 下划线，且以字母开头（前端正则已校验）
   */
  moduleCode: string
  /** 排序位（新增时取当前栏目下模块数 → 追加末尾） */
  sortOrder: number
}

/**
 * 新增 / 编辑模块
 * POST /setting/module/save
 * @returns { code, data?: { id } }
 */
export const saveSettingModule = (payload: ModuleSavePayload) =>
  request(`${serveName}/setting/module/save`, null, 'POST_JSON', payload)

/**
 * 删除模块
 * POST /setting/module/delete
 *
 * **后端校验**：若模块下还有 del_flag=0 的参数，会返回 msg「请先删除模块下所有参数」
 * 该错误提示由全局 request 拦截器统一 toast
 */
export const deleteSettingModule = (id: number) =>
  request(`${serveName}/setting/module/delete`, null, 'POST_JSON', { id })

/**
 * 模块批量排序的单项数据
 */
export interface ModuleSortItem {
  id: number
  sortOrder: number
}

/**
 * 批量更新模块排序
 * POST /setting/module/sort
 *
 * 拖拽完成后前端把当前栏目下所有模块的最新顺序一次性提交
 * sortOrder 从 0 起递增即可，后端按提交顺序覆盖
 */
export const sortSettingModules = (columnId: number, sortList: ModuleSortItem[]) =>
  request(`${serveName}/setting/module/sort`, null, 'POST_JSON', { columnId, sortList })

// ===== 参数（Param）=====

/**
 * 查询模块下参数列表
 * POST /setting/param/list
 * @returns { code, data: ParamVO[] }
 */
export const getSettingParams = (moduleId: number) =>
  request(`${serveName}/setting/param/list`, null, 'POST_JSON', { moduleId })

/**
 * 新增 / 编辑参数的请求体
 *
 * paramValue 存储格式按 paramSource 不同而不同：
 *   - CUSTOM_TEXT：原始字符串（短文本或长文本）
 *   - CONTACTS：JSON 字符串 `[{userId,userName,name}]`
 *   - APP：JSON 字符串 `[{dbtId,appName}]`
 *   - CHANNEL：JSON 字符串 `["payChnl1","payChnl2"]`
 *
 * paramValueType 用于前端渲染时区分 input/textarea/multi-select 三种控件
 */
export interface ParamSavePayload {
  /** null = 新增；非空 = 编辑 */
  id: number | null
  /** 所属模块 id */
  moduleId: number
  /** 参数名称（前端已校验非空） */
  paramName: string
  /** 参数编码（首次保存后后端会锁定不可改） */
  paramCode: string
  /** 参数来源 */
  paramSource: ParamSource
  /** 参数值；枚举型来源时为 JSON 数组字符串 */
  paramValue: string
  /** 控件类型 */
  paramValueType: ParamValueType
  /** 备注（可选） */
  remark?: string
}

/**
 * 新增 / 编辑参数
 * POST /setting/param/save
 * 后端校验：枚举型 source 对应的 paramValue 必须为合法 JSON 数组
 * @returns { code, data: { id } }
 */
export const saveSettingParam = (payload: ParamSavePayload) =>
  request(`${serveName}/setting/param/save`, null, 'POST_JSON', payload)

/**
 * 删除参数
 * POST /setting/param/delete
 */
export const deleteSettingParam = (id: number) =>
  request(`${serveName}/setting/param/delete`, null, 'POST_JSON', { id })

// ===== 来源候选项 =====

/**
 * 来源候选项查询请求
 * keyword 为预留搜索关键字；当前所有 sourceType 都查全部，不分页
 */
export interface SourceOptionsQuery {
  /** 仅支持 'CHANNEL'（当前阶段）；CONTACTS / APP 候选项接口待后端提供 */
  sourceType: string
  /** 搜索关键字（预留，CHANNEL 暂未使用） */
  keyword?: string
}

/**
 * 参数来源候选项查询
 * POST /setting/param/source/options
 *
 * 当前后端仅支持 CHANNEL（payChnl 枚举）；CONTACTS / APP 暂无候选项接口，前端弹窗对应来源返回空数组
 *
 * 不传 page/size，由后端返回全部候选项（前端不做分页）
 *
 * @returns { code, data: { total: number, list: Array<{value,label}> } }
 */
export const getSettingParamSourceOptions = (query: SourceOptionsQuery) =>
  request(`${serveName}/setting/param/source/options`, null, 'POST_JSON', {
    keyword: '',
    ...query,
  })

// ===== 变更日志（ParamLogModal 弹窗使用）=====

/**
 * 变更日志查询请求
 */
export interface ParamLogQuery {
  paramId: string
  pageNum?: number
  pageSize?: number
}

/**
 * 参数变更日志查询
 * POST /setting/param/log
 *
 * 底层查询：operation_log WHERE biz_type='SETTING_PARAM' AND biz_id=paramId
 * 按 create_time DESC 排序
 *
 * @returns { code, msg, data: ParamLogItem[], count: number }
 */
export const getSettingParamLogs = (query: ParamLogQuery) =>
  request(`${serveName}/setting/param/log`, null, 'POST_JSON', {
    pageNum: 1,
    pageSize: 20,
    ...query,
  })
